import SectionHeading from "@/pages/home/components/SectionHeading";
import { hotRankList, hotTags } from "@/mocks/home";

const trendMap: Record<string, { icon: string; className: string; label: string }> = {
  up: { icon: "ri-arrow-up-line", className: "text-primary-600", label: "上升" },
  flat: { icon: "ri-subtract-line", className: "text-accent-700", label: "持平" },
  down: { icon: "ri-arrow-down-line", className: "text-foreground-500", label: "回落" },
};

export default function HotRankSection() {
  return (
    <section id="hot-rank" className="hl-anchor w-full bg-background-100">
      <div className="px-4 py-14 md:px-6 md:py-16 lg:px-8">
        <SectionHeading
          eyebrow="Real-time Ranking"
          title="黑料51瓜友热议榜"
          desc="按实时热度排序，每 10 分钟刷新一次。榜单只呈现讨论度，不代表任何事实判断，请瓜友自行核对原始线索。"
          actionLabel="查看完整榜单"
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <ol className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {hotRankList.map((item, index) => {
                const trend = trendMap[item.trend];
                return (
                  <li
                    key={item.id}
                    className="border-b border-background-200 last:border-b-0"
                  >
                    <a
                      href="#latest"
                      className="group flex items-start gap-4 p-4 transition-colors hover:bg-primary-50/60 md:gap-5 md:p-5"
                    >
                      <span
                        className={`w-8 shrink-0 font-heading text-2xl font-black leading-none md:text-3xl ${
                          index < 3 ? "text-primary-600" : "text-foreground-300"
                        }`}
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded bg-secondary-100 px-2 py-0.5 text-[11px] font-bold text-secondary-900">
                            {item.tag}
                          </span>
                          <span
                            className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${trend.className}`}
                          >
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className={`${trend.icon} text-xs`} />
                            </span>
                            {trend.label}
                          </span>
                        </div>

                        <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                          {item.title}
                        </h3>
                        <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
                          {item.summary}
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
                          <span className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-primary-600">
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className="ri-fire-line text-xs" />
                            </span>
                            热度 {item.heat}
                          </span>
                          <span className="inline-flex items-center gap-1 whitespace-nowrap">
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className="ri-chat-3-line text-xs" />
                            </span>
                            {item.comments}
                          </span>
                        </div>
                      </div>

                      <span className="flex h-8 w-8 shrink-0 items-center justify-center self-center rounded-full border border-background-300 text-foreground-500 transition-colors group-hover:border-primary-300 group-hover:text-primary-600">
                        <i className="ri-arrow-right-up-line text-base" />
                      </span>
                    </a>
                  </li>
                );
              })}
            </ol>
          </div>

          <aside className="flex flex-col gap-6 lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                  <i className="ri-flashlight-line text-sm" />
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950">热议关键词</h3>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {hotTags.map((tag) => (
                  <a
                    key={tag}
                    href="#latest"
                    className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
              <h3 className="font-heading text-base font-bold text-primary-900">榜单说明</h3>
              <ul className="mt-4 space-y-3">
                <li className="flex gap-2.5 text-xs leading-relaxed text-primary-900/80">
                  <span className="mt-1 flex h-3 w-3 shrink-0 items-center justify-center">
                    <i className="ri-checkbox-blank-circle-fill text-[6px]" />
                  </span>
                  热度由阅读、讨论、收藏三项行为加权得出，仅反映关注度。
                </li>
                <li className="flex gap-2.5 text-xs leading-relaxed text-primary-900/80">
                  <span className="mt-1 flex h-3 w-3 shrink-0 items-center justify-center">
                    <i className="ri-checkbox-blank-circle-fill text-[6px]" />
                  </span>
                  同一话题在 24 小时内不重复上榜，避免刷榜干扰。
                </li>
                <li className="flex gap-2.5 text-xs leading-relaxed text-primary-900/80">
                  <span className="mt-1 flex h-3 w-3 shrink-0 items-center justify-center">
                    <i className="ri-checkbox-blank-circle-fill text-[6px]" />
                  </span>
                  如发现线索失实，可在讨论圈提交旁证，编辑会复核。
                </li>
              </ul>
            </div>

            <a
              href="#community"
              className="flex items-center justify-between rounded-lg bg-foreground-950 px-5 py-4 transition-colors hover:bg-foreground-900"
            >
              <span className="flex flex-col">
                <span className="font-heading text-base font-bold text-background-50">
                  去讨论圈聊聊
                </span>
                <span className="mt-1 text-xs text-background-100/60">今日已有 2,140 条新回复</span>
              </span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-background-50">
                <i className="ri-arrow-right-line text-base" />
              </span>
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
}